'use client';

import { useState } from 'react';
import { ChevronDown, ChevronRight, Check, Bell, Sparkles, Square, CheckSquare } from 'lucide-react';
import { useStore, type ActiveFilter, type TVFilter, type MovieFilter } from '@/lib/store';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface HierarchicalFilterProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function HierarchicalFilter({ open, onOpenChange }: HierarchicalFilterProps = {}) {
  const { activeFilters, toggleFilter, selectAllTV, selectAllMovies, clearAllFilters, watchlist } = useStore();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['tv', 'movies']));
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isOpen = open !== undefined ? open : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  // Helper functions to check if item matches filter
  const hasWatchProgress = (item: any) => {
    if (item.type !== 'tv') return false;
    if (item.watchedSeasons && item.watchedSeasons.length > 0) return true;
    if (item.watchedEpisodes) {
      const totalWatchedEpisodes = Object.values(item.watchedEpisodes).reduce(
        (sum: number, episodes: any) => sum + (episodes?.length || 0), 0
      );
      if (totalWatchedEpisodes > 0) return true;
    }
    return false;
  };

  const isWatching = (item: any) => {
    if (item.type !== 'tv' || !item.currentlyWatching) return false;
    // Don't show if completed
    if (item.totalSeasons && item.watchedSeasons.length >= item.totalSeasons) {
      return false;
    }
    return true;
  };

  const isComplete = (item: any) => {
    if (item.type !== 'tv') return false;
    // Show as complete if all seasons are watched
    if (item.totalSeasons && item.watchedSeasons && item.watchedSeasons.length >= item.totalSeasons) {
      return true;
    }
    return false;
  };

  const hasNewContent = (item: any) => {
    if (item.type !== 'tv') return false;
    return item.hasNewContent && item.status !== 'Ended' && item.status !== 'Cancelled';
  };

  const isRecentlyAdded = (item: any, days: number = 14) => {
    if (!item.addedAt) {
      return false;
    }

    try {
      const addedDate = new Date(item.addedAt);
      const now = new Date();
      const daysSinceAdded = (now.getTime() - addedDate.getTime()) / (1000 * 60 * 60 * 24);

      // Only show in Recently Added if:
      // 1. Added within the last X days (default 14)
      // 2. Has no watch progress (not started watching)
      // 3. Not currently marked as watching
      if (item.type === 'tv') {
        return daysSinceAdded <= days && !hasWatchProgress(item) && !item.currentlyWatching;
      } else if (item.type === 'movie') {
        return daysSinceAdded <= days && !item.watched;
      }
    } catch (error) {
      // Silently handle date parsing errors
    }
    return false;
  };

  // Count items for each filter - using exclusive logic to match displayed counts
  // Each count excludes items that appear in higher-priority sections

  // Priority 1: Recently Added (no exclusions needed - highest priority for new unwatched items)
  const tvRecentlyAddedCount = watchlist.filter(item =>
    item.type === 'tv' && isRecentlyAdded(item) && !isWatching(item)
  ).length;

  // Priority 2: New Content (exclude Recently Added, Watching)
  const tvNewCount = watchlist.filter(item =>
    hasNewContent(item) && !isWatching(item) && !isRecentlyAdded(item)
  ).length;

  // Priority 4: In Progress (exclude Watching, Recently Added, New Content)
  const tvInProgressCount = watchlist.filter(item =>
    item.type === 'tv' &&
    hasWatchProgress(item) &&
    !item.currentlyWatching &&
    !isComplete(item) &&
    !isWatching(item) &&
    !isRecentlyAdded(item) &&
    !hasNewContent(item)
  ).length;

  // Priority 5: Unwatched (exclude Watching, Recently Added, New Content)
  const tvUnwatchedCount = watchlist.filter(item =>
    item.type === 'tv' &&
    !hasWatchProgress(item) &&
    !item.currentlyWatching &&
    !isWatching(item) &&
    !isRecentlyAdded(item) &&
    !hasNewContent(item)
  ).length;

  // Priority 6: Complete (exclude Watching, Recently Added, New Content)
  const tvCompleteCount = watchlist.filter(item =>
    isComplete(item) &&
    !isWatching(item) &&
    !isRecentlyAdded(item) &&
    !hasNewContent(item)
  ).length;

  const movieRecentlyAddedCount = watchlist.filter(item => item.type === 'movie' && isRecentlyAdded(item)).length;
  const movieUnwatchedCount = watchlist.filter(item =>
    item.type === 'movie' && !item.watched && !isRecentlyAdded(item)
  ).length;
  const movieCompleteCount = watchlist.filter(item =>
    item.type === 'movie' && item.watched
  ).length;

  const tvFilters: TVFilter[] = ['tv-recently-added', 'tv-new', 'tv-in-progress', 'tv-unwatched', 'tv-complete'];
  const movieFilters: MovieFilter[] = ['movie-recently-added', 'movie-unwatched', 'movie-complete'];

  const allTVSelected = tvFilters.every(f => activeFilters.includes(f));
  const allMoviesSelected = movieFilters.every(f => activeFilters.includes(f));
  const someTVSelected = tvFilters.some(f => activeFilters.includes(f)) && !allTVSelected;
  const someMoviesSelected = movieFilters.some(f => activeFilters.includes(f)) && !allMoviesSelected;

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleGroupToggle = (groupType: 'tv' | 'movies') => {
    if (groupType === 'tv') {
      if (allTVSelected) {
        // Deselect all TV filters
        const newFilters = activeFilters.filter(f => !tvFilters.includes(f as TVFilter));
        useStore.getState().setFilters(newFilters);
      } else {
        selectAllTV();
      }
    } else {
      if (allMoviesSelected) {
        // Deselect all movie filters
        const newFilters = activeFilters.filter(f => !movieFilters.includes(f as MovieFilter));
        useStore.getState().setFilters(newFilters);
      } else {
        selectAllMovies();
      }
    }
  };

  const filterOptions = [
    { id: 'tv-recently-added', label: 'Recently Added', count: tvRecentlyAddedCount, group: 'tv', icon: <Sparkles className="h-3.5 w-3.5" /> },
    { id: 'tv-new', label: 'New Content', count: tvNewCount, group: 'tv', icon: <Bell className="h-3.5 w-3.5" /> },
    { id: 'tv-in-progress', label: 'In Progress', count: tvInProgressCount, group: 'tv' },
    { id: 'tv-unwatched', label: 'Unwatched', count: tvUnwatchedCount, group: 'tv' },
    { id: 'tv-complete', label: 'Complete', count: tvCompleteCount, group: 'tv' },
    { id: 'movie-recently-added', label: 'Recently Added', count: movieRecentlyAddedCount, group: 'movies', icon: <Sparkles className="h-3.5 w-3.5" /> },
    { id: 'movie-unwatched', label: 'Unwatched', count: movieUnwatchedCount, group: 'movies' },
    { id: 'movie-complete', label: 'Complete', count: movieCompleteCount, group: 'movies' },
  ];

  const getDisplayValue = () => {
    if (activeFilters.length === 0) return 'All Content';
    if (allTVSelected && allMoviesSelected) return 'All Content';
    if (allTVSelected) return 'All TV Shows';
    if (allMoviesSelected) return 'All Movies';

    const selected = filterOptions.filter(opt => activeFilters.includes(opt.id as ActiveFilter));
    if (selected.length === 1) {
      const filter = selected[0];
      return filter.group === 'tv' ? `TV: ${filter.label}` : `Movies: ${filter.label}`;
    }
    return `${selected.length} filters`;
  };

  return (
    <Select
      value="custom"
      onValueChange={() => {}}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <SelectTrigger className="w-full sm:w-auto sm:flex-shrink-0 h-11 rounded-full bg-white/10 ring-1 ring-white/15 border-0 px-4 text-white hover:bg-white/15 backdrop-blur-md transition-all font-medium text-sm gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/60 justify-center">
        <span className="flex-1 text-center">{getDisplayValue()}</span>
      </SelectTrigger>
      <SelectContent
        className="bg-gray-900 border-gray-800 w-full sm:w-[320px] max-h-[60vh] overflow-y-auto"
        sideOffset={8}
        align="start"
        side="bottom"
        position="popper"
      >
        {/* TV Shows Section */}
        <div className="border-b border-gray-800">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleGroup('tv');
            }}
            className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-red-600/20 focus:bg-red-600/20 transition-colors text-white"
          >
            <div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleGroupToggle('tv');
              }}
              className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center cursor-pointer"
            >
              {allTVSelected ? (
                <CheckSquare className="h-4 w-4 text-red-600" />
              ) : someTVSelected ? (
                <CheckSquare className="h-4 w-4 text-red-600/70" />
              ) : (
                <Square className="h-4 w-4 text-gray-400" />
              )}
            </div>
            <span className="flex-1 font-semibold">TV Shows</span>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              {expandedGroups.has('tv') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </span>
          </button>

          {expandedGroups.has('tv') && (
            <div className="bg-gray-900/50">
              {filterOptions.filter(opt => opt.group === 'tv').map((option) => {
                const isSelected = activeFilters.includes(option.id as ActiveFilter);
                return (
                  <button
                    key={option.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleFilter(option.id as ActiveFilter);
                    }}
                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-red-600/20 focus:bg-red-600/20 transition-colors"
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      {isSelected && <Check className="h-4 w-4" />}
                    </span>
                    <span className={cn("flex-1", isSelected ? "text-white" : "text-gray-400")}>
                      {option.label}
                    </span>
                    {option.icon && <span className="ml-1">{option.icon}</span>}
                    {option.count !== undefined && option.count > 0 && (
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full",
                        isSelected ? "bg-red-600/20 text-red-300" : "bg-gray-800 text-gray-500"
                      )}>
                        {option.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Movies Section */}
        <div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleGroup('movies');
            }}
            className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-red-600/20 focus:bg-red-600/20 transition-colors text-white"
          >
            <div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleGroupToggle('movies');
              }}
              className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center cursor-pointer"
            >
              {allMoviesSelected ? (
                <CheckSquare className="h-4 w-4 text-red-600" />
              ) : someMoviesSelected ? (
                <CheckSquare className="h-4 w-4 text-red-600/70" />
              ) : (
                <Square className="h-4 w-4 text-gray-400" />
              )}
            </div>
            <span className="flex-1 font-semibold">Movies</span>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              {expandedGroups.has('movies') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </span>
          </button>

          {expandedGroups.has('movies') && (
            <div className="bg-gray-900/50">
              {filterOptions.filter(opt => opt.group === 'movies').map((option) => {
                const isSelected = activeFilters.includes(option.id as ActiveFilter);
                return (
                  <button
                    key={option.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleFilter(option.id as ActiveFilter);
                    }}
                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-red-600/20 focus:bg-red-600/20 transition-colors"
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      {isSelected && <Check className="h-4 w-4" />}
                    </span>
                    <span className={cn("flex-1", isSelected ? "text-white" : "text-gray-400")}>
                      {option.label}
                    </span>
                    {option.icon && <span className="ml-1">{option.icon}</span>}
                    {option.count !== undefined && option.count > 0 && (
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full",
                        isSelected ? "bg-red-600/20 text-red-300" : "bg-gray-800 text-gray-500"
                      )}>
                        {option.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Clear All Option */}
        {activeFilters.length > 0 && (
          <div className="border-t border-gray-800">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearAllFilters();
              }}
              className="relative flex w-full cursor-pointer select-none items-center justify-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-red-600/20 focus:bg-red-600/20 transition-colors text-red-400 font-medium"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
